/**
 * Story 052 — TanStack Query mutation hooks for /me/* mutations.
 *
 * Per `tanstack-query-conventions` skill: mutations don't retry. On
 * success the relevant query keys are invalidated.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "./authFetch.js";
import { accountKeys } from "./keys.js";
import type { AddressFields, MeExpanded, Role } from "./types.js";

export interface ProfilePatchBody {
  firstName?: string;
  lastName?: string;
  nickName?: string;
  displayName?: string;
  preferredLanguage?: "en" | "de";
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProfilePatchBody) =>
      authFetch<MeExpanded>("/me", { method: "PATCH", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountKeys.me() });
    },
  });
}

export interface EmailChangeBody {
  newEmail: string;
  currentPassword: string;
}

export function useChangeEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EmailChangeBody) =>
      authFetch<{ verificationPending: boolean }>("/me/email", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountKeys.me() });
    },
  });
}

export interface PhoneChangeBody {
  // BE DTO (UpdateMyPhoneBodyDto) requires `newPhone` (E.164). The
  // legacy `phoneNumber` shape predated 4.0.0 and produced a 400 from
  // class-validator (`newPhone must be a string`). SB6-AUTH-PWD-01.
  newPhone: string;
  currentPassword: string;
}

export function useChangePhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PhoneChangeBody) =>
      authFetch<{ verificationPending: boolean }>("/me/phone", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountKeys.me() });
    },
  });
}

export function useUpdateAddress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AddressFields) =>
      authFetch<{ ok: true }>("/me/address", { method: "PUT", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountKeys.me() });
    },
  });
}

export function useUploadAvatar() {
  // Note: NO `onSuccess: invalidateQueries` — the caller (ProfileSection)
  // patches the cached `me` queries directly via `setQueryData` to keep
  // the optimistic avatar URL stable. Invalidating would refetch and
  // clobber the new URL with the (still avatar-less) /me response.
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return authFetch<{ avatarUrl: string }>("/me/avatar", {
        method: "POST",
        formData: fd,
      });
    },
  });
}

export function useDeleteAvatar() {
  return useMutation({
    mutationFn: () => authFetch<undefined>("/me/avatar", { method: "DELETE" }),
  });
}

export interface PasswordChangeBody {
  // BE DTO (ChangeMyPasswordBodyDto) requires `currentPassword`. The
  // historical `oldPassword` shape predated auth/v1@4.0.0 and produced
  // a 400 from class-validator (`currentPassword must be a string`).
  // SB6-AUTH-PWD-01.
  currentPassword: string;
  newPassword: string;
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: PasswordChangeBody) =>
      authFetch<undefined>("/me/password", { method: "POST", body }),
  });
}

export function usePasswordResetRequest() {
  return useMutation({
    mutationFn: (email: string) =>
      authFetch<undefined>("/password-reset/request", {
        method: "POST",
        body: { email },
      }),
  });
}

export interface UpdateRoleBody {
  // Canonical 7-role enum (auth/v1@4.3.0 / Story 054a / BE-058).
  role: Role;
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateRoleBody) =>
      authFetch<{ ok: true }>("/me/role", { method: "PATCH", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountKeys.me() });
    },
  });
}

export function useTerminateOtherSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      authFetch<undefined>("/me/sessions/terminate-others", {
        method: "POST",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountKeys.sessions() });
    },
  });
}

export function useTerminateSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      authFetch<undefined>(`/me/sessions/${sessionId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accountKeys.sessions() });
    },
  });
}

export interface MfaEnrollBody {
  type: "totp" | "webauthn" | "sms";
}

export function useEnrollMfa() {
  return useMutation({
    mutationFn: (body: MfaEnrollBody) =>
      authFetch<{ enrollmentUrl: string }>("/me/mfa/enroll", {
        method: "POST",
        body,
      }),
  });
}

export function useRequestDataExport() {
  return useMutation({
    mutationFn: () =>
      authFetch<undefined>("/me/data-export-request", { method: "POST" }),
  });
}

export function useRequestDeletion() {
  return useMutation({
    mutationFn: () =>
      authFetch<undefined>("/me/deletion-request", { method: "POST" }),
  });
}
